import "./CreateInvoiceModalReplacementSKU.css";
import "./DiscountType.css";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import {
  getCreateInvoicePopupSKUReplacementMetaDataValues,
  getCreateInvoiceSKUReplacementPopupMetaData,
  initialDiscountState,
} from "./ViewOrderUtils";
import { useState, useEffect, KeyboardEvent } from "react";
import { CreateInvoiceModalReplacementSKUProps } from "./ViewOrderType";
import Switch from "react-switch";
import DiscountType from "./DiscountType";
import CircularProgress from "@mui/material/CircularProgress";
import { Tooltip } from "@mui/material";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import { truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import useShortcutKeys from "../../components/dms_common/ShortcutKeys";
import SimpleBar from "simplebar-react";
import BigDecimal from "js-big-decimal";
import "simplebar-react/dist/simplebar.min.css";
const CreateInvoiceModalReplacementSKU: React.FC<CreateInvoiceModalReplacementSKUProps> = ({
  modalOpen,
  handleModalClose,
  orderData,
  handleOnSave,
  discount,
  setDiscount,
  invoiceLoading,
  hideAdditionalDistributorDiscount = false,
  originalSkuFulfillmentMap,
  newOrderData,
  skuNameMap,
  orderWillGet,
  showBothHeaderLevelDiscountedAmountAndPercentage
}) => {
  const markDiscountType = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.markDiscountType;
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<string>("0");
  const [discountPercentage, setDiscountPercentage] = useState<string>("0");
  const [replacementExpanded, setReplacementExpanded] = useState(false);

  useEffect(() => {
  const hundred = new BigDecimal(100);
  const one = new BigDecimal(1);
  const orderNetAmount = new BigDecimal(orderData?.netAmount || 0);
  const inputValue = new BigDecimal(truncateToTwoDecimals(Number(discount?.discountValue || "0")));
  let discountValue = new BigDecimal(0);
  let discountPercent = new BigDecimal(0);
    if(!discount.discountType || discount?.discountValue?.length === 0) return;
    if(discount?.discountValue === "0") {
      setDiscountAmount("0");
      setDiscountPercentage("0");
      setFinalAmount(Number(truncateToTwoDecimals(Number(orderNetAmount.getValue()))));
      return;
    }

  if (markDiscountType) {
    if ("markup" === markDiscountType?.toLowerCase()) {
      if (discount?.discountType === "VALUE") {
        // Given: Value, Calculate: Percentage (Reverse Markup)
        // Formula: percentage = ((netAmount / (netAmount - value)) - 1) * 100
        discountValue = inputValue;
        if (parseFloat(orderNetAmount.getValue()) > 0) {
          const denominator = orderNetAmount.subtract(inputValue);
          discountPercent = orderNetAmount.divide(denominator).subtract(one).multiply(hundred);
        } else {
          discountPercent = new BigDecimal(0);
        }
        setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
        setDiscountPercentage(discountPercent.getValue());
      } else {
        // Given: Percentage, Calculate: Value (Markup)
        // Formula: value = netAmount - (netAmount / (1 + percentage/100))
        discountPercent = inputValue;
        const divisor = one.add(inputValue.divide(hundred));
        discountValue = orderNetAmount.subtract(orderNetAmount.divide(divisor));
        setDiscountAmount(discountValue.getValue());
        setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
      }
    } else { // markdown
      if (discount?.discountType === "VALUE") {
        // Given: Value, Calculate: Percentage (Reverse Standard)
        // Formula: percentage = (value / netAmount) * 100
        discountValue = inputValue;
        if (parseFloat(orderNetAmount.getValue()) > 0) {
          discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
        } else {
          discountPercent = new BigDecimal(0);
        }
        setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
        setDiscountPercentage(discountPercent.getValue());
      } else {
        // Given: Percentage, Calculate: Value (Standard)
        // Formula: value = netAmount * (percentage / 100)
        discountPercent = inputValue;
        discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
        setDiscountAmount(discountValue.getValue());
        setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
      }
    }
  } else {
    // No mark discount (default behavior - standard calculation)
    if (discount?.discountType === "VALUE") {
      // Given: Value, Calculate: Percentage (Reverse Standard)
      discountValue = inputValue;
      if (parseFloat(orderNetAmount.getValue()) > 0) {
        discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
      } else {
        discountPercent = new BigDecimal(0);
      }
      setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
      setDiscountPercentage(discountPercent.getValue());
    } else {
      // Given: Percentage, Calculate: Value (Standard)
      discountPercent = inputValue;
      discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
      setDiscountAmount(discountValue.getValue());
      setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
    }
  }

  const finalAmount = orderNetAmount.subtract(discountValue);
  setFinalAmount(Number(truncateToTwoDecimals(Number(finalAmount.getValue()))));
}, [orderData, discount, markDiscountType]);

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      setDiscount(initialDiscountState);
    } else {
      setDiscount((prevDiscount) => ({
        ...prevDiscount,
        discountApplied: checked,
      }));
    }
  };

  const handleRadioToggle = (value: "VALUE" | "PERCENTAGE") => {
    setDiscount(prevDiscount => ({
      ...prevDiscount,
      discountValue: "0",
      discountType: value,
    }));
  };

  const handleDiscountValueChange = (value: string) => {
    setDiscount((prevDiscount) => ({
      ...prevDiscount,
      discountValue: value,
    }));
  };

  const handleResetChanges = () => {
    setDiscount({ ...initialDiscountState, discountApplied: true });
  };

  const reset = () => {
    setDiscount(initialDiscountState);
    setFinalAmount(0);
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;
  let createInvoiceOrderDetailsMetaDataValues: any = {};
  createInvoiceOrderDetailsMetaDataValues = getCreateInvoicePopupSKUReplacementMetaDataValues(newOrderData);

  const designationEnabled = designationBasedUomSecondary?.enable ?? false;
  const availableDesignations =
    designationBasedUomSecondary?.roles?.designationKeys?.map((item: { type: any }) => item?.type) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => currentDesignation.includes(type));
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);

  const handleSwitchKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSwitchChange(!discount?.discountApplied);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "a" && modalOpen) {
        event.preventDefault();
        if (!invoiceLoading) {
          handleOnSave("");
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        reset();
        handleModalClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleOnSave, invoiceLoading, handleModalClose]);

  const { positiveCta } = useShortcutKeys();

  // Flatten all replacement SKUs
  const allReplacementRows = Array.from(originalSkuFulfillmentMap || []).flatMap(
    ([originalSku, info]) =>
      Array.from(info.replacements.entries()).map(([replacementSku, qty]) => ({
        originalSku,
        replacementSku,
      }))
  );

  return (
    <Modal
      open={modalOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="create-invoice-modal-container-replacement">
        <ScreenOverlay apply={invoiceLoading} />
        <Tooltip title={orderData?.orderNumber}>
          <div className="createInvoiceModalHeader">
            <p>Create Invoice for : {orderData?.orderNumber}</p>
            <button
              onClick={() => {
                reset();
                handleModalClose();
              }}
              style={{ border: "none", background: "none" }}
            >
              <CloseIcon style={{ cursor: "pointer" }} />
            </button>
          </div>
        </Tooltip>

        {/* Replacement SKU Summary */}
        <div className="replacementSKU-summary" onScroll={(e) => e.stopPropagation()}>
          <div style={{ fontWeight: "bold" }}>SKU Replacement Summary:</div>
          <div>
            Some SKUs in your order will be replaced due to stock unavailability:
          </div>
          <SimpleBar
            className={`replaced-sku-summary ${replacementExpanded ? "expanded" : ""}`}
            style={{
              maxHeight: replacementExpanded ? "140px" : "80px",
              height: "auto",
              overflowY: replacementExpanded ? "auto" : "hidden",
              position: "relative",
              transition: "all 0.3s ease-in-out"
            }}
          >
            {allReplacementRows.map((row, idx) => (
               
              <div className="replaced-sku-row" key={idx}>
                <span className="replacement-sku-block">
                  <span className="original-sku">{row.originalSku}</span> -{" "}
                  <Tooltip title={skuNameMap?.get(row.originalSku) || ""} arrow>
                    <span className="replacement-sku-name">{skuNameMap?.get(row.originalSku)}</span>
                  </Tooltip>
                </span>
                <span className="arrow"> → </span>
                <span className="replacement-sku-block">
                  <span className="replacement-sku">{row.replacementSku}</span> -{" "}
                  <Tooltip title={skuNameMap?.get(row.replacementSku) || ""} arrow>
                    <span className="replacement-sku-name">{skuNameMap?.get(row.replacementSku)}</span>
                  </Tooltip>
                </span>
              </div>
            ))}

            
          </SimpleBar>
          {allReplacementRows.length > 4 && (
              <div
                style={{
                  textAlign: "left",
                  marginTop: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  color: "#2a82e1",
                }}
                onClick={() => setReplacementExpanded((prev) => !prev)}
              >
                {`${replacementExpanded ? "View Less" : "View More"}`}
              </div>
            )}
        </div>

        {/* Order Details */}
        <div className="createInvoice-orderDetails-replacement"
        style={{
          maxHeight: replacementExpanded ? "54px" : "400px",
          cursor: replacementExpanded ? "pointer" : 'default',
          justifyContent: "flex-end",
          height: "auto",
          position: "relative",
          transition: "all 0.3s ease-in-out",
          overflow: "hidden"
        }}
        onClick={() => setReplacementExpanded(false)}
        >
          {getCreateInvoiceSKUReplacementPopupMetaData()?.map((obj: any) => {
            const { key, value, color, currencySymbol, multiQty } = obj;

            if (
              designationEnabled &&
              designation &&
              designationKeys &&
              matchedRole &&
              key === "Total Ordered Quantity"
            ) {
              const elementToKeep = matchedRole?.uomAllowed?.case ? "QTY(CS)" : "QTY(PC)";
              if (elementToKeep !== key) return null;
            }

            if (multiQty && Array.isArray(value)) {
              const uomLabels: Record<string, string> = {
                csQty: ConfigDictionary.caseUnitLabel || "CS",
                pcQty: ConfigDictionary.packUnitLabel || "PC",
                otherQty: ConfigDictionary.otherUnitLabel || "Oth",
              };

              return (
                <div className="createInvoice-orderDetailTile" key={key}>
                  <span style={color ? { color: color, fontWeight: 500 } : {}}>{key}</span>
                  <span className="multiqty-value" style={color ? { color: color, fontWeight: 600 } : {}}>
                    {value.map((subKey: string, index: number) => (
                      <span key={index} style={{ }}>
                        {currencySymbol ? ConfigDictionary.currency : ""}
                        {createInvoiceOrderDetailsMetaDataValues?.[subKey]}{" "}
                        <strong>{uomLabels[subKey] ?? ""}</strong>
                      </span>
                    ))}
                  </span>
                </div>
              );
            }

            return (
              <div className="createInvoice-orderDetailTile" key={key}>
                <span style={color ? { color: color, fontWeight: 500 } : {}}>{key}</span>
                <span style={color ? { color: color, fontWeight: 600 } : {}}>
                  {currencySymbol ? ConfigDictionary.currency : ""}
                  {createInvoiceOrderDetailsMetaDataValues?.[value]}
                </span>
              </div>
            );
          })}
        </div>

        {/* Additional Discount */}
        {!hideAdditionalDistributorDiscount && (
          <div className="add-discount-container">
            <div className="toggleContainer">
              <p>Additional distributor discount</p>
              <Switch
                onKeyDown={handleSwitchKeyDown}
                onChange={handleSwitchChange}
                checked={discount?.discountApplied}
                offColor="#9ba09f"
                onColor="#00C6B1"
                handleDiameter={16.2}
                uncheckedIcon={false}
                checkedIcon={false}
                height={20.7}
                width={42.6}
              />
            </div>

            {discount?.discountApplied && (
              <div className="discount-configure-container">
                <DiscountType
                  type="Value"
                  handleRadioToggle={() => handleRadioToggle("VALUE")}
                  discount={discount}
                  handleDiscountValueChange={handleDiscountValueChange}
                  maxVal={orderData?.netAmount}
                  discountAmount={discountAmount}
                  discountPercentage={discountPercentage}
                  showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
                />
                <DiscountType
                  type="Percentage"
                  handleRadioToggle={() => handleRadioToggle("PERCENTAGE")}
                  discount={discount}
                  labelSuffix="%"
                  handleDiscountValueChange={handleDiscountValueChange}
                  maxVal={100}
                  discountAmount={discountAmount}
                  discountPercentage={discountPercentage}
                  showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
                />
              </div>
            )}

            {discount?.discountApplied && (
              <div className="final-amount-container">
                <p>
                  Final amount (after additional discount):{" "}
                  <span>
                    {ConfigDictionary.currency}
                    {finalAmount}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer Buttons */}
        <div className="createInvoice-footerContainer" style={{justifyContent: "flex-end"}}>
          {
            (orderWillGet !== "INVOICED") && 
            <span style={{color: "red", fontSize: "12px", fontStyle: "italic",width: "100%", alignItems: "start"}}>
              {
                (orderWillGet === "REJECTED") ?
                <div style={{display: "flex", flexDirection: "column", alignItems: "start"}}>
                  <span>Order will get rejected due to unavailability of stock.</span>
                  <span>Do you wish to proceed?</span>
                </div>
                : (orderWillGet === "PARTIALLY INVOICED") ?
                <div style={{display: "flex", flexDirection: "column", alignItems: "start"}}>
                  <span>Order will be partially invoiced due to partial availability of stock.</span>
                  <span>Do you wish to proceed?</span>
                </div>
                :
                <></>
              }
            </span>
          }
          <div style={{display: "flex", gap: "10px", whiteSpace: "nowrap"}}>
          {+discount?.discountValue > 0 && (
            <button className="invoice-reset-changes" onClick={handleResetChanges}>
              <span>Reset</span>
            </button>
          )}
          <button
            className="invoice-reset-changes"
            style={{ backgroundColor: "#00C6B1", borderColor: "#00C6B1" }}
            onClick={() => handleOnSave("")}
            disabled={invoiceLoading}
          >
            <span
              style={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {invoiceLoading ? (
                <CircularProgress
                  style={{
                    width: 20,
                    height: 20,
                    color: "#fff",
                  }}
                />
              ) : (
                "Proceed"
              )}
            </span>
            {!invoiceLoading && <span style={{fontSize:"9.5px", color: "#fff"}}>{positiveCta}</span>}
          </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateInvoiceModalReplacementSKU;
